<template>
  <div class="p-mb-3">
    <div class="search-bar" style="float: right">
      <i class="pi pi-search" />
      <InputText v-model="filters['global'].value" :placeholder="$t('factory.appliances.search')" style="width:70px" @update:modelValue="freeSearch" :aria-label="$t('factory.appliances.search')"/>
      <i class="pi pi-times" @click="() => {filters['global'].value=null; freeSearch();}"/>
    </div>
    <div class="p-formgroup-inline p-ai-center">
      <div v-if="email" class="p-field">
        <span class="admin-user-email">{{email}}</span>
      </div>
      <div class="p-field">
        <Dropdown id="gamma" v-model="filters['gamma'].value" :options="gamme" optionLabel="name" optionValue="code"
        :placeholder="$t('factory.appliances.filter_gamma_ph')" @change="changeFilters" />
      </div>
      <div class="p-field">
        <Dropdown id="modelNumber" v-model="filters['modelNumber'].value" :options="modelNumbers" optionLabel="name" optionValue="code"
          :placeholder="$t('factory.appliances.filter_model_ph')" @change="changeFilters" />
      </div>
      <div v-if="email==null" class="p-field">
        <Dropdown id="domains" v-model="filters['domain'].value" :options="domains" optionLabel="name" optionValue="code"
          :placeholder="$t('factory.appliances.filter_brand_ph')" @change="changeFilters" />
      </div>
      <div class="p-field">
        <Dropdown id="firmwareVersions" v-model="filters['currentFirmware'].value" :options="firmwareVersions" optionLabel="name" optionValue="code"
          :placeholder="$t('factory.appliances.filter_firmware_ph')" @change="changeFilters" />
      </div>
      <div class="p-field">
        <ToggleButton v-model="statusButton" :onLabel="$t('factory.appliances.filter_all')" :offLabel="$t('factory.appliances.filter_online')"  @change="changeFilters"  :aria-label="$t('common.online')+'/'+$t('common.offline')" />      
      </div>
    </div>

    <div v-if="email!=null && (userRole.includes('FACTORY') || userRole === 'SUBSIDIARY') && (role==='USER' || role.includes('SERVICE'))" class="display-when-breakpoint">
      <span class="button-header" @click="shareDialog()"><i class="pi pi-plus" /> {{$t('factory.appliances.col_add')}}</span>
    </div>

    <DataTable :value="appliances" stripedRows 
      v-model:filters="filters" responsiveLayout="stack" 
      breakpoint="960px" :selectionMode="selectionMode" dataKey="serialNumber" @rowSelect="onRowSelect"  
      :autoLayout="true" sortField="serialNumber" :sortOrder="1"
      :paginator="true" paginatorPosition="bottom" paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink" 
      v-model:first="table.first" :rows="table.rows" :totalRecords="table.totalRecords" :lazy="true"
      @update:first="reloadAppliances" 
      :currentPageReportTemplate="'{totalRecords} ' + $t('factory.appliances.counter')">
      <template #empty>
          {{$t('factory.appliances.empty_list')}}
      </template>

      <Column field="serialNumber" :header="$t('factory.appliances.col_serialNumber')" ></Column>

      <Column field="modelNumber" :header="$t('factory.appliances.col_model')" >
        <template #body="slotProps">
          <div>
            {{$functions.fixModelName(slotProps.data.modelNumber)}} 
            <img v-if="slotProps.data.online" src="assets/irinox/icons/status_online_small.svg" class="p-pl-1"  :alt="$t('common.online')"/>
            <img v-else src="assets/irinox/icons/status_offline_small.svg" class="p-pl-1"  :alt="$t('common.offline')"/>
          </div>
        </template>
      </Column>
      
      <Column v-if="email==null" field="domain" :header="$t('factory.appliances.col_brand')" ></Column>
      <Column v-else field="role" :header="$t('factory.appliances.col_role')" >
        <template #body="slotProps">
          <span>{{$t('roles.'+slotProps.data.roleAppliance)}}</span>
        </template>
      </Column>

      <Column field="currentFirmware" :header="$t('factory.appliances.col_firmware')" ></Column>

      <Column field="plans" :header="$t('service.appliances.col_services')" class="p-p-1">
        <template #body="slotProps">
          <div>
            <div v-if="hasPlanActive(slotProps.data, 'PLUS')" class="plan-badge">
              <div class="plan-plus">
                <img src="/assets/irinox/icons/plans/plus.svg" :alt="$t('service.appliances.table_badge_active_plus')" />
              </div>
              <span>{{ getPlanExpireDate(slotProps.data, 'PLUS') }}</span>
            </div>
            <div v-else-if="hasPlanActive(slotProps.data, 'PREMIUM')" class="plan-badge">
              <div class="plan-premium">
                <img src="/assets/irinox/icons/plans/premium.svg" :alt="$t('service.appliances.table_badge_active_premium')" />
              </div>
              <span>{{ getPlanExpireDate(slotProps.data, 'PREMIUM') }}</span>
            </div>
            <div v-else-if="hasPlanActivable(slotProps.data, 'PLUS') && !hasPlanActivable(slotProps.data, 'PREMIUM') && !hasPlanActive(slotProps.data, 'PREMIUM')" class="plan-badge">
              <div class="plan-plus small">{{$t('service.appliances.table_badge_activable_plus')}}</div>
            </div>
            <div v-else-if="hasPlanActivable(slotProps.data, 'PREMIUM') && !hasPlanActive(slotProps.data, 'PLUS')" class="plan-badge">
              <div class="plan-premium small">{{$t('service.appliances.table_badge_activable_premium')}}</div>
            </div>
            <div v-if="hasPlanActive(slotProps.data, 'GELATERIA')" class="plan-badge">
              <div class="plan-gelateria">
                <img src="/assets/irinox/icons/plans/gelateria.svg" :alt="$t('service.appliances.table_badge_active_gelateria')" />
              </div>
              <span>{{ getPlanExpireDate(slotProps.data, 'GELATERIA') }}</span>
            </div>
            <div v-else-if="hasPlanActivable(slotProps.data, 'GELATERIA')" class="plan-badge">
              <div class="plan-gelateria small">{{$t('service.appliances.table_badge_activable_gelateria')}}</div>
            </div>
          </div>
        </template>
      </Column>

      <Column bodyClass="" class="irinox-table-buttons p-text-right">
        <template #header>
          <span class="sr-only">{{$t('common.actions')}}</span>
          <span v-if="(userRole.includes('FACTORY') || userRole === 'SUBSIDIARY') && email!=null && (role==='USER' || role.includes('SERVICE'))" class="button-header" @click="shareDialog()">
            <i class="pi pi-plus" /> {{$t('factory.appliances.col_add')}}
          </span>
        </template>
        <template #body="slotProps">
          <div class="button-body">
            <i v-if="slotProps.data.installationReport" class="icon-report" style="font-size: 24px; cursor: pointer"
              @click="openDownload($event, slotProps.data.installationReport)"
              v-tooltip.left="$t('report.tooltip_download')" />
            <img v-if="(userRole.includes('FACTORY') || userRole === 'SUBSIDIARY')" src="assets/irinox/icons/download_zip.svg" 
                @click="downloadCertificates(slotProps.data.serialNumber)" v-tooltip.left="$t('factory.appliances.tooltip_certificates')" :alt="$t('factory.appliances.tooltip_certificates')"/>

            <img src="assets/irinox/icons/device.svg" @click="openApplianceDetails(slotProps.data.serialNumber)" v-tooltip.left="$t('factory.appliances.tooltip_details')" :alt="$t('factory.appliances.tooltip_details')"/>

            <img src="assets/irinox/icons/haccp_text.svg" @click="openHaccp(slotProps.data.serialNumber)" v-tooltip.left="$t('factory.appliances.tooltip_haccp')" :alt="$t('factory.appliances.tooltip_haccp')"/>

            <img v-if="userRole.includes('FACTORY') && email!=null && role==='USER'" src="assets/irinox/icons/promote.svg" 
                @click="promoteOp($event, slotProps.data.serialNumber)" v-tooltip.left="$t('factory.appliances.tooltip_promote')" :alt="$t('factory.appliances.tooltip_promote')"/>

            <img v-if="(userRole.includes('FACTORY') || userRole === 'SUBSIDIARY') && email!=null && (role==='USER' || role.includes('SERVICE'))" src="assets/irinox/icons/unshare.svg" 
                @click="unshareDialog(slotProps.data.serialNumber)" v-tooltip.left="$t('factory.appliances.tooltip_unshare')" :alt="$t('factory.appliances.tooltip_unshare')"/>

          </div>
        </template>
      </Column>
    </DataTable>

    <ReportDownloadOverlay ref="downloadOverlay" />
    
    <OverlayPanel ref="op" class="download-op" style="width: 200px">
      <div @click="promoteDialog($event, 'ADMIN')">{{$t('roles.ADMIN')}}</div>
      <div @click="promoteDialog($event, 'KITCHEN_MANAGER')">{{$t('roles.KITCHEN_MANAGER')}}</div>
    </OverlayPanel>

  </div>

  <!--        -->
  <!-- ERRORS -->
  <!--        -->
  <IrinoxDialogDark :visible="dialog.error" 
    mode="error" 
    :message="$t('factory.error_dialog')"
    :life="3000" 
    @update:visible="dialog.error = $event" />

  <!--         -->
  <!-- PROMOTE -->
  <!--         -->
  <IrinoxDialogDark :visible="dialog.promote" mode="confirm" 
    :leftB="$t('factory.promote_dialog_cancel_button')" @click:left-button="()=>{dialog.promote=!dialog.promote}" 
    :rightB="$t('factory.promote_dialog_ok_button')" @click:right-button="promote" 
    :message="dialog.promoteText"
    icon="assets/irinox/icons/promote_big.svg" />
  <IrinoxDialogDark :visible="dialog.promoteSuccess" 
    mode="success" 
    :message="$t('factory.promote_dialog_success')"
    :life="3000" 
    @update:visible="dialog.promoteSuccess = $event" />

  <!--         -->
  <!-- UNSHARE -->
  <!--         -->
  <IrinoxDialogDark :visible="dialog.unshare" mode="confirm" 
    :leftB="$t('factory.unshare_dialog_cancel_button')" @click:left-button="()=>{dialog.unshare=!dialog.unshare}" 
    :rightB="$t('factory.unshare_dialog_ok_button')" @click:right-button="unshare" 
    :message="$t('factory.unshare_dialog')"
    icon="assets/irinox/icons/unshare_big.svg" />
  <IrinoxDialogDark :visible="dialog.unshareSuccess" 
    mode="success" 
    :message="$t('factory.unshare_dialog_success')"
    :life="3000" 
    @update:visible="dialog.unshareSuccess = $event" />

  <!--              -->
  <!-- SHARE DIALOG -->
  <!--              -->
  <ShareAppliancesDialogFactory v-if="email!=null" :visible="dialog.share" 
    :preselectedAppliances="selectedAppliances"
    @update:visible="closeShareDialog"
    @click:left-button="closeShareDialog" 
    @click:right-button="closeShareDialog"/>

  <!--            -->
  <!-- ADD DIALOG -->
  <!--            -->
  <ShareDialog v-if="email!=null" :visible="dialog.add" 
    mode="factory"
    :appliances="selectedAppliances"
    :email="email"
    :role="role"
    @update:visible="dialog.add = $event" 
    @click:button="addShare" 
    @click:open-share="openShareDialog"/>
  <IrinoxDialogDark :visible="dialog.addSuccess" 
    mode="success" 
    :message="$t('admin.users.add_dialog_success_edit')"
    :life="3000" 
    @update:visible="dialog.addSuccess = $event" />

  <IrinoxDialogDark :visible="dialog.downloadCertificatesSuccess" 
    mode="success" 
    :message="$t('factory.appliances.download_certificates_success')"
    :life="3000" 
    @update:visible="dialog.downloadCertificatesSuccess = $event" />

</template>

<script>
import AccountService from '../../service/AccountService';
import ApplianceService from '../../service/ApplianceService';
import IrinoxDialogDark from '../IrinoxDialogDark.vue';
import ShareDialog from '../ShareDialog.vue';
import ShareAppliancesDialogFactory from '../ShareAppliancesDialogFactory.vue';
import ReportDownloadOverlay from '../report/ReportDownloadOverlay.vue';
import {FilterMatchMode} from 'primevue/api';
import { mapGetters } from 'vuex';

export default {
    components: {
        IrinoxDialogDark,
        ShareDialog,
        ShareAppliancesDialogFactory,
        ReportDownloadOverlay,
    },
    props: {
      email: String,
      role: String
    },
    data() {
        return {
          filters: {
              'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
              'modelNumber': {value: null, matchMode: FilterMatchMode.EQUALS},
              'gamma': {value: null, matchMode: FilterMatchMode.EQUALS},
              'domain': {value: null, matchMode: FilterMatchMode.EQUALS},
              'currentFirmware': {value: null, matchMode: FilterMatchMode.EQUALS},
              'online': {value: null, matchMode: FilterMatchMode.EQUALS}
          },
          modelNumbers: [{ 
              code: null,
              name: this.$t('factory.appliances.filter_remove') 
            }],
          gamme: [{ 
              code: null,
              name: this.$t('factory.appliances.filter_remove') 
            }],
          domains: [{ 
              code: null,
              name: this.$t('factory.appliances.filter_remove') 
            }],
          firmwareVersions: [{ 
              code: null,
              name: this.$t('factory.appliances.filter_remove') 
            }],
          statusButton: false,
          item: {
            serialNumber: null,
            role: null
          },
          selectedAppliances: [],
          dialog: {
            error: false,
            unshare: false,
            unshareSuccess: false,
            share: false,
            add: false,
            addSuccess: false,
            downloadCertificatesSuccess: false
          },
          table: {
            rows: 50,
            totalRecords: 0,
            first: 0
          },
          freeSearchTimer: null,
        };
    },
    computed: {
        ...mapGetters({
          personaltoken: 'userdata/personaltoken',
          appliances: 'appliances/appliances',
          userRole: 'userdata/role',
          uxData: 'preferences/uxData',
        }),
        selectionMode: function() {
          return this.email!=null ? null : 'single';
        }
    },
    watch: {
      // statusButton: 'statusButtonToggle',
      // 'filters': {
      //   handler: 'changeFilters',
      //   deep: true
      // }
      // "filters.global.value": "freeSearch",
      // "filters.domain.value": "changeFilters",
      // "filters.currentFirmware.value": "changeFilters",
      // "filters.online.value": "changeFilters",
      // "filters.modelNumber.value": "changeFilters",
      // "filters.gamma.value": "changeFilters",
    },
    created() {
      this.accountService = new AccountService();
      this.applianceService = new ApplianceService();
    },
    mounted() {
      this.$store.commit('appliances/appliances', [])
      if(!this.email) {
        this.$store.commit('preferences/addUxData', {"factory.tab": "appliances"});
        this.filters = {...this.filters, ...(this.$functions.clone(this.uxData["factory.appliances.filters"] ?? {}))};
        if(this.filters['online'].value) {
          this.statusButton = true;
        } else {
          this.statusButton = false;
        }
        this.table.first = this.uxData["factory.appliances.paginationFirst"] ?? 0;
      }
      this.reloadFilters();
      this.reloadAppliances();
    },
    methods: {
      freeSearch() {
        clearTimeout(this.freeSearchTimer);
        this.freeSearchTimer = setTimeout(() => {
          this.changeFilters();
        }, 1000 );
      },
      reloadFilters() {
        let gs = [];
        let mns = [];
        let ds = [];
        let fvs = [];
        let emptyData = { 
          code: null,
          name: this.$t('factory.appliances.filter_remove') 
        };

        this.applianceService.getModelsForFilter(this.personaltoken, this.email)
          .then((models) => {
            for(const model of models) {
              gs.push({ 
                code: model.gamma, 
                name: this.$functions.fixGamma(model.gamma) 
              });
              mns.push({ 
                code: model.gamma + "|||" + model.modelName, 
                name: this.$functions.fixModelName(model.gamma + " " + model.modelName) 
              });
            }
            this.gamme = this.$functions.uniqByKeepFirst(gs, item => item.code).sort((a,b) => this.$functions.compareStrings(a,b,'name'));
            this.modelNumbers = this.$functions.uniqByKeepFirst(mns, item => item.code).sort((a,b) => this.$functions.compareStrings(a,b,'name'));
          })
          .catch(error => {
            console.error("FILTER MODELS ERROR", error);
          })
          .finally(() => {
            this.gamme.unshift(emptyData);
            this.modelNumbers.unshift(emptyData);
          });

        this.applianceService.getCurrentFirmwareForFilter(this.personaltoken, this.email)
          .then((currentFirmwares) => {
            for(const currentFirmware of currentFirmwares) {
              fvs.push({ code: currentFirmware, name: currentFirmware });
            }
            this.firmwareVersions = this.$functions.uniqByKeepFirst(fvs, item => item.code).sort((a,b) => this.$functions.compareStrings(a,b,'name'));
          })
          .catch(error => {
            console.error("FILTER FIRMWARES ERROR", error);
          })
          .finally(() => {
            this.firmwareVersions.unshift(emptyData);
          });

        this.applianceService.getDomainsForFilter(this.personaltoken, this.email)
          .then((domains) => {
            for(const domain of domains) {
              ds.push({ code: domain, name: domain });
            }
            this.domains = this.$functions.uniqByKeepFirst(ds, item => item.code).sort((a,b) => this.$functions.compareStrings(a,b,'name'));
          })
          .catch(error => {
            console.error("FILTER DOMAINS ERROR", error);
          })
          .finally(() => {
            this.domains.unshift(emptyData);
          });

      },
      changeFilters() {
        if(this.statusButton) {
          this.filters['online'].value = true;
        } else {
          this.filters['online'].value = null;
        }
        if(!this.email) {
          this.$store.commit('preferences/addUxData', {"factory.appliances.filters": this.$functions.clone(this.filters)} );
        }
        this.table.first = 0;
        this.reloadAppliances();
      },
      reloadAppliances() {
        this.applianceService.getFactoryAppliancesFilter(this.personaltoken, this.email, this.filters, this.table)
        .then((appliances) => {
            if(!this.email) {
              this.$store.commit('preferences/addUxData', { "factory.appliances.paginationFirst": this.table.first } );
            }
            this.table.totalRecords = appliances.count;
            this.$store.commit('appliances/appliances', appliances.items);
          })
          .catch(error => {
            console.error("EXCEPTION "+error);
            this.table.totalRecords = 0;
            this.$store.commit('appliances/appliances', []);
          });
      },
      // statusButtonToggle() {
      //   if(this.statusButton) {
      //     this.filters['online'].value = true;
      //   } else {
      //     this.filters['online'].value = null;
      //   }
      // },
      openDownload(event, report) {
        this.$refs.downloadOverlay.open(event, report.serialNumber, report.installationReportId, report.language);
      },
      openApplianceDetails(serialNumber) {
        this.$router.push({ name: 'appliance-details', params: { serialNumber: serialNumber } });
      },
      openHaccp(serialNumber) {
        this.$router.push({ name: 'haccp-reports-historyback', params: { serialNumber: serialNumber }});
      },
      promoteOp(event, serialNumber) {
        this.item.serialNumber = serialNumber;
        this.$refs.op.toggle(event);
      },
      promoteDialog(event, role) {
        this.$refs.op.toggle(event);
        this.item.role = role;
        this.dialog.promoteText = this.$t('factory.promote_dialog').replace("[email]",this.email).replace("[role]",this.$t('roles.'+role));
        this.dialog.promote = true;
      },
      promote() {
        let shareData = {
          email: this.email,
          role: this.item.role,
          appliances: [this.item.serialNumber]
        };

        this.dialog.promote = false;
        this.applianceService.postFactoryShare(this.personaltoken, shareData)
          .then(() => {
            this.dialog.promoteSuccess = true;
            setTimeout(function() {
              this.reloadAppliances();
            }.bind(this),3000);
          })
          .catch(error => {
            console.error("EXCEPTION "+error);
            this.dialog.error = true;
          });

      },
      unshareDialog(serialNumber) {
        this.item.serialNumber = serialNumber;
        this.dialog.unshare = true;
      },
      unshare() {
        this.dialog.unshare = false;
        this.applianceService.deleteFactoryShare(this.personaltoken, this.email, this.item.serialNumber)
          .then(() => {
            this.dialog.unshareSuccess = true;
            setTimeout(function() {
              this.reloadAppliances();
            }.bind(this),3000);
          })
          .catch(error => {
            console.error("EXCEPTION "+error);
            this.dialog.error = true;
          });
      },
      shareDialog() {
        this.selectedAppliances = [];
        this.dialog.add = true;
      },
      openShareDialog() {
        this.dialog.add = false;
        this.dialog.share = true;
      },
      closeShareDialog(appliances) {
        if(appliances) {
          this.selectedAppliances = appliances;
        }
        this.dialog.share = false;
        this.dialog.add = true;
      },
      addShare(shareData) {

        shareData.appliances = [];
        for (let i = 0; i < this.selectedAppliances.length; i++) {
          shareData.appliances.push(this.selectedAppliances[i].serialNumber);
        }

        this.dialog.add = false;

        this.applianceService.postFactoryShare(this.personaltoken, shareData)
          .then(() => {
            this.dialog.addSuccess = true;
            this.reloadAppliances();
          })
          .catch(error => {
            console.error("EXCEPTION "+error);
            this.dialog.error = true;
          });
      },
      onRowSelect(event) {
        if(event.originalEvent.target.className == "button-body" ||
            event.originalEvent.target.parentNode.className == "button-body" ||
            this.email!=null) {
          //SKIP ROW CLICK IF LAST COLUMN
          event.originalEvent.preventDefault();
          return;
        }
        this.$router.push({ name: 'factory-appliance', params: { serialNumber: event.data.serialNumber } });
      },
      downloadCertificates(serialNumber) {
        this.applianceService.downloadCertificates(this.personaltoken, serialNumber)
          .then((res) => {
            let contentType = res.headers["content-type"];
            if(contentType === 'application/octet-stream') {
              this.dialog.downloadCertificatesSuccess = true;
              let filename = "certificates_"+serialNumber+".zip";
              let contentDisposition = res.headers["content-disposition"];
              if(contentDisposition) {
                filename = contentDisposition.replace(/.*filename="(.*)".*/g,"$1");
              }
              let blob=new Blob([res.data], {type: 'application/octet-stream'});
              let link=document.createElement('a');
              link.href=window.URL.createObjectURL(blob);
              link.download=filename;
              link.click();
            } else {
              this.dialog.error = true;
              console.error("EXCEPTION "+JSON.stringify(res));
            }
          })
          .catch(error => {
            console.error("EXCEPTION "+error);
            this.dialog.error = true;
          });
      },
      hasPlanActive(appliance, planCode) {
        return appliance?.plans?.some(plan => plan.code === planCode && plan.startDate !=null);
      },
      hasPlanActivable(appliance, planCode) {
        return appliance?.plans?.some(plan => plan.code === planCode && plan.startDate == null);
      },
      getPlanExpireDate(appliance, planCode) {
        const plan = appliance?.plans?.find(plan => plan.code === planCode);
        return plan ? this.$d(plan.endDate) : null;
      },
    }
}
</script>

<style scoped>

.irinox-form .p-dropdown {
  width: 200px;
}

</style>

<style>


</style>